import { useMutation } from "@tanstack/react-query";
import { useContext, useState } from "react";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import {
	createLtlMutationErrorHandler,
	LTL_PORTAL_ERROR_MESSAGE,
} from "~/features/quote/utils/ltl-error-handler.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import type {
	SendCustomerDocsBody,
	SendCustomerDocsToRecipientsBody,
} from "~/shared/schemas/send-customer-docs-schema.ts";
import type { SendDocsBody } from "~/shared/schemas/send-docs-schema.ts";
import { sendDocsResponseSchema } from "~/shared/schemas/send-docs-schema.ts";
import type {
	ShippingLabelReportBody,
	ShippingLabelReportResponse,
} from "~/shared/schemas/shipping-label-report-schema.ts";
import { shippingLabelReportResponseSchema } from "~/shared/schemas/shipping-label-report-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getBinaryResponse,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import type { AddressType } from "~/shared/types/shipment-summary-types.ts";
import { environmentConfig } from "~/shared/utils/environment-config.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import type { LabelSize } from "../constants/tender-modal-constants";

export type TenderFormData = {
	labelSize: LabelSize;
	labelQuantity: number;
	additionalDetails: string;
};

export const useShippingLabelReport = () => {
	return useMutation({
		mutationFn: async (body: ShippingLabelReportBody) => {
			return await postAndValidate(
				apiRoutes.SHIPPINGLABELREPORT,
				body,
				shippingLabelReportResponseSchema,
			);
		},
	});
};

const fetchBol = async (params: { poNumber: number; quoteId: number }) => {
	const url = `${apiRoutes.BOLREPORT.replace(
		":poNumber",
		String(params.poNumber),
	)}?quoteId=${params.quoteId}`;
	return await getBinaryResponse(url);
};

export const useBolReport = () => {
	return { fetchBol };
};

// Shared helper function to build shipping label report body
const buildShippingLabelReportBody = (
	formData: TenderFormData,
	quoteState: QuoteStateType,
	selectedAddresses: AddressType,
): ShippingLabelReportBody => {
	return {
		customerId: String(quoteState.customer.id),
		carrierName:
			quoteState.carrierPrices.find(
				(c) => c.id === quoteState.selectedCarrierPriceId,
			)?.carrier.displayName ?? "",
		poNumber: quoteState.poNumber ?? 0,
		quoteId: quoteState.id,
		numberOfLabels: formData.labelQuantity,
		labelsPerPage: formData.labelSize.labelSize === "fullPage" ? 1 : 6,
		labelSize: formData.labelSize.labelSize,
		rotate: false,
		showSequenceNumber: true,
		additionalDetails: formData.additionalDetails,
		pickupName: selectedAddresses.pickup.companyName ?? "",
		pickupAddress1: selectedAddresses.pickup.address1,
		pickupAddress2: selectedAddresses.pickup.address2 ?? "",
		pickupCityState: `${quoteState.pickupPostalCode.city.name}, ${quoteState.pickupPostalCode.city.state.abbreviation}`,
		pickupZip: quoteState.pickupPostalCode.postalCode,
		deliveryName: selectedAddresses.delivery.companyName ?? "",
		deliveryAddress1: selectedAddresses.delivery.address1,
		deliveryAddress2: selectedAddresses.delivery.address2 ?? "",
		deliveryCityState: `${quoteState.deliveryPostalCode.city.name}, ${quoteState.deliveryPostalCode.city.state.abbreviation}`,
		deliveryZip: quoteState.deliveryPostalCode.postalCode,
		retrieveDocuments: true,
	};
};

// Helper function to wait for a specified duration
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Helper function to retry shipping label report with pro number support
const fetchShippingLabelWithRetry = async (
	body: ShippingLabelReportBody,
	maxRetries = 6,
	retryDelayMs = 5000,
): Promise<{
	result: ShippingLabelReportResponse | null;
	timedOut: boolean;
	error?: string;
}> => {
	const onError = createLtlMutationErrorHandler(LTL_PORTAL_ERROR_MESSAGE);

	// Initial request
	const initialResult = await postAndValidate(
		apiRoutes.SHIPPINGLABELREPORT,
		body,
		shippingLabelReportResponseSchema,
		{ onError },
	);

	// Handle initial request error
	if (!initialResult.success) {
		return {
			result: null,
			timedOut: false,
			error: initialResult.error,
		};
	}

	// If carrier doesn't support pro number or pro number is already ready, return immediately
	if (
		!initialResult.data.carrierProNumberSupported ||
		initialResult.data.proNumberReady
	) {
		return { result: initialResult.data, timedOut: false };
	}

	// Retry logic for carriers that support pro number
	let attempts = 0;
	let lastResult = initialResult.data;

	while (attempts < maxRetries && !lastResult.proNumberReady) {
		attempts++;
		await wait(retryDelayMs);

		const retryResult = await postAndValidate(
			apiRoutes.SHIPPINGLABELREPORT,
			body,
			shippingLabelReportResponseSchema,
			{ onError },
		);

		// Handle retry error - continue with last successful result
		if (!retryResult.success) {
			console.error("Retry request failed:", retryResult.error);
			break;
		}

		lastResult = retryResult.data;

		if (lastResult.proNumberReady) {
			return { result: lastResult, timedOut: false };
		}
	}

	// If we exhausted all retries and pro number is still not ready
	return { result: lastResult, timedOut: !lastResult.proNumberReady };
};

const getTrackingBaseUrl = () => {
	const env = environmentConfig.environmentName;
	if (env === "prod") return "https://trax.tql.com";
	if (env === "stage") return "https://trax2stage.tql.com";
	return "https://trax2dev.tql.com";
};

// Shared docs-email helpers so the internal and external (recipients) flows
// stay in sync on subject text, tracking link copy, and recipient parsing.
const buildDefaultSubject = (poNumber: number) =>
	`Your BOL & Shipping Label is ready | PO# ${poNumber}`;

const buildTrackingLinkText = (poNumber: number) =>
	`\nClick the link below to track TQL PO# ${poNumber}.\nWhen we receive new statuses from the carrier, we will update the page for your reference.\n${getTrackingBaseUrl()}/shipment-tracking?searchNumber=${poNumber}`;

const buildInitialDocsFormValues = (email: string, poNumber: number) => ({
	to: email,
	subject: buildDefaultSubject(poNumber),
	message: "",
});

// Read-only preview helpers for the external recipients flow. These mirror the
// subject/body the backend generates server-side (see ShippingLabelBOLNotification
// in the QuoteMgmt domain) so the customer sees an accurate preview. The values
// are display-only — they are NOT sent to the API (the recipients payload only
// carries recipients + includeTrackingLink). The recipients flow always includes
// the shipping label, so the "and Shipping Label are" wording is always used.
const buildCustomerSubject = (poNumber: number) =>
	`Your BOL and Shipping Label are Ready | PO # ${poNumber}`;

const buildCustomerMessagePreview = (poNumber: number) =>
	`Hello,\n\nYour BOL and Shipping Label are attached for PO# ${poNumber}`;

const buildCustomerTrackingLinkText = (poNumber: number) =>
	`\n\nClick the link below to track TQL PO# ${poNumber}. When we receive new statuses from the carrier, we will update the page for your reference.\n${getTrackingBaseUrl()}/shipment-tracking?searchNumber=${poNumber}`;

const buildCustomerInitialFormValues = (email: string, poNumber: number) => ({
	to: email,
	subject: buildCustomerSubject(poNumber),
	message: buildCustomerMessagePreview(poNumber),
});

const parseToEmails = (to: string) =>
	to
		.split(",")
		.map((email) => email.trim())
		.filter((email) => email.length > 0);

// Sanitize filename parts (remove invalid characters and replace spaces)
const sanitizeFilename = (str: string) =>
	str.replaceAll(/[^\d A-Za-z-]/g, "").replaceAll(/\s+/g, "-");

export const useGenerateReports = () => {
	const { fetchBol } = useBolReport();
	const [isBolFetching, setIsBolFetching] = useState(false);
	const [isGeneratingReports, setIsGeneratingReports] = useState(false);
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { showError, showWarning } = useNotifications();

	const getShippingLabelReportBody = (
		formData: TenderFormData,
	): ShippingLabelReportBody => {
		return buildShippingLabelReportBody(
			formData,
			quoteState,
			selectedAddressBookItems as AddressType,
		);
	};

	const generateReports = async (
		formData: TenderFormData,
		handlePdf: (pdf: string | ArrayBuffer, filename: string) => void,
	) => {
		setIsGeneratingReports(true);
		const shippingLabelReportBody = getShippingLabelReportBody(formData);

		const poNumber = sanitizeFilename(
			String(quoteState.poNumber ?? "") || quoteState.poNumbers || "Unknown",
		);

		try {
			const { result, timedOut, error } = await fetchShippingLabelWithRetry(
				shippingLabelReportBody,
			);

			if (error) {
				console.error("Shipping label report request failed:", error);
				showError(error);
			} else if (result) {
				// Show warning if timed out waiting for pro number
				if (timedOut) {
					showWarning(
						"The carrier pro number is not ready yet. Please try generating the shipping label again later.",
						{ autoClose: false },
					);
				}

				// Still handle the PDF even if timed out
				handlePdf(result.pdf, `${poNumber}_Shipping-Labels.pdf`);
			}
		} catch (error) {
			console.error("Error submitting reports:", error);
			showError(LTL_PORTAL_ERROR_MESSAGE);
		}

		setIsBolFetching(true);
		const bolResult = await fetchBol({
			poNumber: shippingLabelReportBody.poNumber,
			quoteId: shippingLabelReportBody.quoteId,
		});
		setIsBolFetching(false);

		if (bolResult.success) {
			handlePdf(bolResult.data, `${poNumber}_BOL.pdf`);
		} else {
			console.error("BOL report request failed", bolResult.error);
			// Mirrors legacy `email-bol-modal` / `load-tendered-modal` behavior of
			// surfacing a user-visible error when the BOL PDF cannot be generated.
			showError("Failed to generate Bill of Lading PDF");
		}

		setIsGeneratingReports(false);
	};

	return { generateReports, isBolFetching, isGeneratingReports };
};

export type SendDocsFormData = {
	to: string;
	subject: string;
	message: string;
	/**
	 * External recipients flow only: whether to include the tracking link in the
	 * server-generated email. Ignored by the internal flow, which embeds the
	 * tracking link directly in the message body.
	 */
	includeTrackingLink?: boolean;
};

export type SendDocsHook = {
	getInitialFormValues: () => { to: string; subject: string; message: string };
	getTrackingLinkText: () => string;
	buildSendDocsBody: (formData: SendDocsFormData) => SendDocsBody;
	handleSubmit: (
		formData: SendDocsFormData,
		onSuccess?: () => void,
		onError?: (error: Error) => void,
	) => void;
	cancelEmail: () => void;
	isLoading: boolean;
	isSuccess: boolean;
	isError: boolean;
};

export const useSendDocsWithData = (
	labelFormData: TenderFormData,
	options?: { timeoutMs?: number },
): SendDocsHook => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { email } = useIsAuthenticated();

	const [abortController, setAbortController] =
		useState<AbortController | null>(null);
	const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
	const [isPreparingToSend, setIsPreparingToSend] = useState(false);

	const sendDocsMutation = useMutation({
		mutationFn: async ({
			body,
			signal,
		}: {
			body: SendDocsBody;
			signal?: AbortSignal;
		}) => {
			return await postAndValidate(
				apiRoutes.SENDDOCS,
				body,
				sendDocsResponseSchema,
				{ signal },
			);
		},
	});

	const getInitialFormValues = () =>
		buildInitialDocsFormValues(email, quoteState.poNumber ?? 0);

	const getTrackingLinkText = () =>
		buildTrackingLinkText(quoteState.poNumber ?? 0);

	const buildSendDocsBody = (formData: SendDocsFormData): SendDocsBody => {
		const poNumber = quoteState.poNumber ?? 0;
		return {
			subject: formData.subject,
			toEmails: parseToEmails(formData.to),
			poNumber: poNumber,
			messageBody: formData.message,
			shippingLabelReport: buildShippingLabelReportBody(
				labelFormData,
				quoteState,
				selectedAddressBookItems as AddressType,
			),
		};
	};

	const handleSubmit = (
		formData: SendDocsFormData,
		onSuccess?: () => void,
		onError?: (error: Error) => void,
	) => {
		const body = buildSendDocsBody(formData);
		const controller = new AbortController();
		setAbortController(controller);
		setIsPreparingToSend(true);

		// Add a delay before sending the request to allow user to cancel
		const id = setTimeout(() => {
			setIsPreparingToSend(false);
			sendDocsMutation.mutate(
				{ body, signal: controller.signal },
				{
					onSuccess: (result) => {
						setAbortController(null);
						setTimeoutId(null);
						// `postAndValidate` resolves with `{ success: false }` on HTTP
						// errors instead of throwing, so guard against treating a failed
						// response as a success.
						if (result.success) {
							onSuccess?.();
						} else {
							console.error("Failed to send email:", result.error);
							onError?.(new Error(result.error));
						}
					},
					onError: (error) => {
						setAbortController(null);
						setTimeoutId(null);
						console.error("Failed to send email:", error);
						onError?.(error);
					},
				},
			);
		}, options?.timeoutMs ?? 3000); // Configurable timeout, default 3 seconds

		setTimeoutId(id);
	};

	const cancelEmail = () => {
		if (abortController) {
			abortController.abort();
			setAbortController(null);
		}
		if (timeoutId) {
			clearTimeout(timeoutId);
			setTimeoutId(null);
		}
		setIsPreparingToSend(false);
		sendDocsMutation.reset();
	};

	return {
		getInitialFormValues,
		getTrackingLinkText,
		buildSendDocsBody,
		handleSubmit,
		cancelEmail,
		isLoading: sendDocsMutation.isPending || isPreparingToSend,
		isSuccess: sendDocsMutation.isSuccess,
		isError: sendDocsMutation.isError,
	};
};

// Builds the customer-facing shipping label report payload shared by the
// external send flows (with and without editable recipients).
const buildCustomerShippingLabelReport = (
	formData: TenderFormData,
	quoteState: QuoteStateType,
	selectedAddresses: AddressType,
): SendCustomerDocsBody["shippingLabelReport"] => {
	return {
		customerId: String(quoteState.customer.id),
		carrierName:
			quoteState.carrierPrices.find(
				(c) => c.id === quoteState.selectedCarrierPriceId,
			)?.carrier.displayName ?? "",
		poNumber: quoteState.poNumber ?? 0,
		quoteId: quoteState.id,
		numberOfLabels: formData.labelQuantity,
		labelsPerPage: formData.labelSize.labelSize === "fullPage" ? 1 : 6,
		labelSize: formData.labelSize.labelSize,
		rotate: true,
		showSequenceNumber: true,
		additionalDetails: formData.additionalDetails,
		pickupName: selectedAddresses.pickup.companyName ?? "",
		pickupAddress1: selectedAddresses.pickup.address1,
		pickupAddress2: selectedAddresses.pickup.address2 ?? "",
		pickupCityState: `${quoteState.pickupPostalCode.city.name}, ${quoteState.pickupPostalCode.city.state.abbreviation}`,
		pickupZip: quoteState.pickupPostalCode.postalCode,
		deliveryName: selectedAddresses.delivery.companyName ?? "",
		deliveryAddress1: selectedAddresses.delivery.address1,
		deliveryAddress2: selectedAddresses.delivery.address2 ?? "",
		deliveryCityState: `${quoteState.deliveryPostalCode.city.name}, ${quoteState.deliveryPostalCode.city.state.abbreviation}`,
		deliveryZip: quoteState.deliveryPostalCode.postalCode,
	};
};

export const useSendCustomerDocs = () => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { showSuccess, showError } = useNotifications();

	const sendCustomerDocsMutation = useMutation({
		mutationFn: async (body: SendCustomerDocsBody) => {
			return await postAndValidate(
				apiRoutes.SENDCUSTOMERDOCS,
				body,
				sendDocsResponseSchema,
			);
		},
		onSuccess: (response) => {
			if (response.success) {
				showSuccess("Email sent successfully!");
			} else {
				showError("Failed to send email. Please try again.");
			}
		},
		onError: (error) => {
			console.error("Failed to send email:", error);
			showError("Failed to send email. Please try again.");
		},
	});

	const buildSendCustomerDocsBody = (
		formData: TenderFormData,
	): SendCustomerDocsBody => {
		const poNumber = quoteState.poNumber ?? 0;
		return {
			poNumber: poNumber,
			shippingLabelReport: buildCustomerShippingLabelReport(
				formData,
				quoteState,
				selectedAddressBookItems as AddressType,
			),
		};
	};

	const sendDocs = (formData: TenderFormData) => {
		const body = buildSendCustomerDocsBody(formData);
		sendCustomerDocsMutation.mutate(body);
	};

	return {
		sendDocs,
		isLoading: sendCustomerDocsMutation.isPending,
		isSuccess: sendCustomerDocsMutation.isSuccess,
		isError: sendCustomerDocsMutation.isError,
	};
};

// External docs-email hook that mirrors `useSendDocsWithData` so it can drive
// the shared `SendDocsScreen`, but posts to the customer (external) endpoint
// with editable recipients. Gated upstream by the
// `traxLtlPostTenderEmailRecipients` LD flag for specific CIDs.
export const useSendCustomerDocsWithData = (labelFormData: TenderFormData) => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { email } = useIsAuthenticated();

	const sendCustomerDocsMutation = useMutation({
		mutationFn: async (body: SendCustomerDocsToRecipientsBody) => {
			return await postAndValidate(
				apiRoutes.SENDCUSTOMERDOCSTORECIPIENTS,
				body,
				sendDocsResponseSchema,
			);
		},
	});

	const getInitialFormValues = () =>
		buildCustomerInitialFormValues(email, quoteState.poNumber ?? 0);

	const getTrackingLinkText = () =>
		buildCustomerTrackingLinkText(quoteState.poNumber ?? 0);

	const buildSendDocsBody = (
		formData: SendDocsFormData,
	): SendCustomerDocsToRecipientsBody => {
		const poNumber = quoteState.poNumber ?? 0;
		return {
			poNumber: poNumber,
			recipients: parseToEmails(formData.to),
			includeTrackingLink: formData.includeTrackingLink ?? false,
			shippingLabelReport: {
				...buildCustomerShippingLabelReport(
					labelFormData,
					quoteState,
					selectedAddressBookItems as AddressType,
				),
				retrieveDocuments: true,
			},
		};
	};

	const handleSubmit = (
		formData: SendDocsFormData,
		onSuccess?: () => void,
		onError?: (error: Error) => void,
	) => {
		const body = buildSendDocsBody(formData);
		sendCustomerDocsMutation.mutate(body, {
			onSuccess: (result) => {
				// `postAndValidate` resolves with `{ success: false }` on HTTP errors
				// instead of throwing, so guard against treating a failed response as
				// a success.
				if (result.success) {
					onSuccess?.();
				} else {
					console.error("Failed to send email:", result.error);
					onError?.(new Error(result.error));
				}
			},
			onError: (error) => {
				console.error("Failed to send email:", error);
				onError?.(error);
			},
		});
	};

	const cancelEmail = () => {
		sendCustomerDocsMutation.reset();
	};

	return {
		getInitialFormValues,
		getTrackingLinkText,
		buildSendDocsBody,
		handleSubmit,
		cancelEmail,
		isLoading: sendCustomerDocsMutation.isPending,
		isSuccess: sendCustomerDocsMutation.isSuccess,
		isError: sendCustomerDocsMutation.isError,
	};
};
